import { Component, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ApiService } from '../../../../../services/api.service';

interface MemoryItem {
  title: string;
  date: string;
  tag: string;
  album?: string;
  image?: string;
  video?: string;
  audio?: string;
  link?: string;
  description?: string;
  className?: string;
  items?: MemoryItem[];
  id?: string;
  albumId?: string;
}

@Component({
  selector: 'app-memories-stories',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './memories-stories.html',
  styleUrls: ['./memories-stories.css']
})
export class MemoriesStoriesComponent {

  constructor(
    private router: Router,
    private apiService: ApiService,
    private cdr: ChangeDetectorRef
  ) {}

  // ─────────────────────────────────────────────
  // LIFECYCLE
  // ─────────────────────────────────────────────
  ngOnInit(): void {
    this.loadAlbums();
    this.loadMemories();
  }

  // ─────────────────────────────────────────────
  // STATE
  // ─────────────────────────────────────────────
  searchText          : string             = '';
  activeTab           : string             = 'All';
  loading             : boolean            = true;

  selectedImage       : string             = '';
  selectedMemoryItem  : MemoryItem | null  = null;
  selectedLink        : string             = '';
  selectedVideo       : string             = '';
  selectedAudio       : string             = '';

  isMediaViewerOpen   : boolean            = false;
  isStoryDialogOpen   : boolean            = false;

  viewerOpen          : boolean            = false;
  selectedAlbumPhotos : string[]           = [];
  currentPhotoIndex   : number             = 0;
  touchStartX         : number             = 0;

  tabs: string[] = ['All', 'Albums', 'Stories', 'Photos', 'Videos', 'Links', 'Voice'];

  allMemories         : MemoryItem[]       = [];
  albums              : MemoryItem[]       = [];
  memories            : MemoryItem[]       = [];

  currentPage  = 1;
  itemsPerPage = 10;
  totalPages   = 1;
  pages        : number[] = [];

  selectedAlbumItems  : MemoryItem[]       = [];
  selectedAlbumName   : string             = '';
  isAlbumOpen         : boolean            = false;

  // ─────────────────────────────────────────────
  // API
  // ─────────────────────────────────────────────
  loadAlbums(): void {
    this.loading = true;
    this.apiService.getMemoriesAlbumList().subscribe({
      next: (res: any[]) => {
        this.albums = res.map((item: any) => ({
          id          : item.id,
          title       : item.type,
          date        : '',
          tag         : 'Albums',
          album       : item.type,
          description : item.type,
          image       : this.getAlbumImage(item.type),
          className   : this.getAlbumClass(item.type),
          items       : []
        }));
        this.mapMemoriesToAlbums();
      },
      error: (err: any) => {
        this.loading = false;
        console.error('Album API Error:', err);
      }
    });
  }

  loadMemories(): void {
    this.apiService.getMemories().subscribe({
      next: (res: any) => {
        const memoryData = res.memories.map((item: any) => {
          const type = item.memoryType?.toUpperCase();

          // FIX: ensure STORY type maps to 'Stories' tag correctly
          const tag =
            type === 'PHOTO' ? 'Photos'  :
            type === 'VIDEO' ? 'Videos'  :
            type === 'VOICE' ? 'Voice'   :
            type === 'LINK'  ? 'Links'   :
            type === 'STORY' ? 'Stories' :
            'Stories'; // default fallback

          return {
            title       : item.title || 'Memory',
            date        : item.createdDate
              ? new Date(item.createdDate).toLocaleDateString('en-GB', {
                  day  : '2-digit',
                  month: 'short',
                  year : 'numeric'
                })
              : '',
            tag,
            description : item.description || '',
            // FIX: Stories carry an image path too; include for both PHOTO and STORY
            image       : (type === 'PHOTO' || type === 'STORY') ? item.albumPath : '',
            video       : type === 'VIDEO' ? item.albumPath : '',
            audio       : type === 'VOICE' ? item.albumPath : '',
            link        : type === 'LINK'  ? item.albumPath : '',
            albumId     : item.albumType
          };
        });

        this.memories = memoryData;
        this.updatePagination();
        this.mapMemoriesToAlbums();
        this.loading = false;
      },
      error: (err: any) => {
        this.loading = false;
        console.error('Memories API Error:', err);
      }
    });
  }

  mapMemoriesToAlbums(): void {
    if (!this.albums.length || !this.memories.length) return;
    this.albums = this.albums.map((album: any) => {
      const matchedItems = this.memories.filter((mem: any) => {
        const memAlbum = (mem.albumId || '').toString().trim();
        const albumId  = (album.id   || '').toString().trim();
        return memAlbum === albumId;
      });
      return { ...album, items: matchedItems };
    });
  }

  // ─────────────────────────────────────────────
  // ALBUM HELPERS
  // ─────────────────────────────────────────────
  getAlbumImage(type: string): string {
    switch (type) {
      case 'Favorites'      : return 'https://cdn-icons-png.flaticon.com/512/833/833472.png';
      case 'Family Events'  : return 'https://cdn-icons-png.flaticon.com/128/3097/3097951.png';
      case 'Holidays'       : return 'https://cdn-icons-png.flaticon.com/128/2627/2627036.png';
      case 'Special Moments': return 'https://cdn-icons-png.flaticon.com/128/13819/13819669.png';
      case 'Videos'         : return 'https://cdn-icons-png.flaticon.com/128/4406/4406124.png';
      case 'None':
      default               : return 'https://cdn-icons-png.flaticon.com/128/3735/3735057.png';
    }
  }

  getAlbumClass(type: string): string {
    switch (type) {
      case 'Favorites'      : return 'album-favorites';
      case 'Family Events'  : return 'album-family';
      case 'Holidays'       : return 'album-holidays';
      case 'Special Moments': return 'album-moments';
      case 'Videos'         : return 'album-videos';
      case 'None'           : return 'album-none';
      default               : return 'album-default';
    }
  }

  // ─────────────────────────────────────────────
  // COMPUTED GETTERS
  // ─────────────────────────────────────────────
  get filteredMemories(): MemoryItem[] {
    const text = this.searchText.toLowerCase();
    let data: MemoryItem[] = [];

    if (this.activeTab === 'All') {
      data = this.memories;
    } else if (this.activeTab === 'Albums') {
      data = this.albums;
    } else {
      data = this.memories.filter(
        (item: MemoryItem) => item.tag.toLowerCase() === this.activeTab.toLowerCase()
      );
    }

    return data.filter((item: MemoryItem) =>
      item.title.toLowerCase().includes(text)                    ||
      item.tag.toLowerCase().includes(text)                      ||
      (item.album?.toLowerCase().includes(text)       ?? false)  ||
      (item.description?.toLowerCase().includes(text) ?? false)
    );
  }

  get groupedAlbums() {
    return this.albums
      .map((album: any) => ({ name: album.title, items: album.items || [] }))
      .filter(group => group.items.length > 0);
  }

  get paginatedMemories(): MemoryItem[] {
    const start = (this.currentPage - 1) * this.itemsPerPage;
    return this.filteredMemories.slice(start, start + this.itemsPerPage);
  }

  // ─────────────────────────────────────────────
  // PAGINATION
  // ─────────────────────────────────────────────
  changePage(page: number): void {
    if (page < 1 || page > this.totalPages) return;
    this.currentPage = page;
  }

  updatePagination(): void {
    this.totalPages = Math.ceil(this.filteredMemories.length / this.itemsPerPage);
    this.pages = Array.from({ length: this.totalPages }, (_, i) => i + 1);
  }

  // ─────────────────────────────────────────────
  // TAB / SEARCH
  // ─────────────────────────────────────────────
  setTab(tab: string): void {
    this.activeTab   = tab;
    this.currentPage = 1;
    this.updatePagination();
  }

  onSearch(): void {
    this.currentPage = 1;
    this.updatePagination();
  }

  goBack(): void {
    this.router.navigate(['/beneficiary-home']);
  }

  // ─────────────────────────────────────────────
  // MEDIA OPENERS / CLOSERS
  // ─────────────────────────────────────────────
  openImage(item: MemoryItem, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    // Reset all state before opening
    this.selectedVideo      = '';
    this.selectedAudio      = '';
    this.selectedLink       = '';
    this.selectedImage      = '';
    this.selectedMemoryItem = null;
    this.isMediaViewerOpen  = false;
    this.isStoryDialogOpen  = false;
    this.cdr.detectChanges();

    setTimeout(() => {
      this.selectedMemoryItem = item;
      this.selectedImage      = item.image || '';

      if (item.tag === 'Stories') {
        this.isStoryDialogOpen = true;
      } else {
        this.isMediaViewerOpen = true;
      }
      this.cdr.detectChanges();
    }, 50);
  }

  closeImage(): void {
    this.isMediaViewerOpen = false;
    setTimeout(() => {
      this.selectedImage      = '';
      this.selectedMemoryItem = null;
      this.cdr.detectChanges();
    }, 100);
  }

  closeStoryDialog(): void {
    this.isStoryDialogOpen = false;
    setTimeout(() => {
      this.selectedMemoryItem = null;
      this.selectedImage      = '';
      this.cdr.detectChanges();
    }, 100);
  }

  openLink(link?: string): void {
    if (!link) return;
    let finalUrl = link.trim();
    if (!/^https?:\/\//i.test(finalUrl)) finalUrl = 'https://' + finalUrl;
    window.open(finalUrl, '_blank', 'noopener,noreferrer');
  }

  closeLink(): void {
    this.selectedLink = '';
  }

  openVideo(item: any, event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();

    this.selectedVideo      = '';
    this.selectedMemoryItem = null;
    this.cdr.detectChanges();

    setTimeout(() => {
      this.selectedMemoryItem = item;
      this.selectedVideo      = item.video;
      this.isMediaViewerOpen  = true;
      this.cdr.detectChanges();

      setTimeout(() => {
        const video = document.querySelector('.split-video-player') as HTMLVideoElement;
        if (video) { video.load(); video.play().catch(() => {}); }
      }, 100);
    }, 50);
  }

  closeVideo(): void {
    this.isMediaViewerOpen = false;
    setTimeout(() => {
      this.selectedVideo      = '';
      this.selectedMemoryItem = null;
      this.cdr.detectChanges();
    }, 100);
  }

  openAudio(audio: string): void {
    this.closeAllPopups();
    this.selectedAudio = audio;
  }

  closeAudio(): void {
    this.selectedAudio = '';
  }

  closeAllPopups(): void {
    this.selectedImage      = '';
    this.selectedMemoryItem = null;
    this.selectedVideo      = '';
    this.selectedAudio      = '';
    this.selectedLink       = '';
    this.isMediaViewerOpen  = false;
    this.isStoryDialogOpen  = false;
  }

  // ─────────────────────────────────────────────
  // ALBUM OPEN / CLOSE
  // ─────────────────────────────────────────────
  openAlbum(group: any): void {
    this.selectedAlbumItems = [...(group.items || [])];
    this.selectedAlbumName  = group.type || group.title || 'Album';
    this.isAlbumOpen        = true;
    this.cdr.detectChanges();
  }

  closeAlbum(): void {
    this.isAlbumOpen        = false;
    this.selectedAlbumItems = [];
    this.selectedAlbumName  = '';
  }

  // ─────────────────────────────────────────────
  // SWIPE / VIEWER
  // ─────────────────────────────────────────────
  closeViewer(): void { this.viewerOpen = false; }

  nextPhoto(): void {
    if (this.currentPhotoIndex < this.selectedAlbumPhotos.length - 1) this.currentPhotoIndex++;
  }

  prevPhoto(): void {
    if (this.currentPhotoIndex > 0) this.currentPhotoIndex--;
  }

  onTouchStart(event: TouchEvent): void {
    this.touchStartX = event.changedTouches[0].screenX;
  }

  onTouchEnd(event: TouchEvent): void {
    const endX = event.changedTouches[0].screenX;
    if (endX < this.touchStartX - 50) this.nextPhoto();
    if (endX > this.touchStartX + 50) this.prevPhoto();
  }

  // ─────────────────────────────────────────────
  // MISC HELPERS
  // ─────────────────────────────────────────────
  hasTag(items: MemoryItem[], tag: string): boolean {
    return items.some(item => item.tag === tag);
  }

  isFavorite(name: string): boolean {
    return name === 'Favorites';
  }

  getAlbumCover(items?: MemoryItem[]): string {
    if (!items || items.length === 0) return '';
    return items[0].image || '';
  }

  getAlbumVideo(items: MemoryItem[]): string {
    return items.find(item => item.video)?.video || '';
  }
}